// «Pris til kunden» på en ordre eller et skiplukk (OrderDetails): prisene kunden skal betale, og
// «Skriv ut prisoversikt» til kassa (tilbud/prisoversikt.js). Prisene kommer fra ordrens egne
// prislinjer, ellers fra et godkjent pristilbud (tilbud laget før 30.10). Linjene er også
// pristilbudet (OrdreTilbud.jsx, vinnsel 30.10), så prisene registreres ett sted.
import React, { useEffect, useState } from 'react';
import { knappPrimar, knappSekundar } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import VareLinjer from './VareLinjer';
import { linjeTall, oppsummering, kr } from './kalkyle';
import { skrivUtPrisoversikt } from './prisoversikt';
import { getSkiSkoyteTjenester } from '../lib/tjenestekatalogApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const harPris = (l) => Number(String(l.pris ?? '').replace(',', '.')) > 0;

// Det prisene starter med: varen på en vanlig ordre (prisen fylles inn), eller «Grunnprepp nye ski»
// (tjenestekatalogen) for parene på et skiplukk som skal ha det. Flyttet fra OrdreTilbud 30.10.
async function startlinjer(ordre, par) {
  if (ordre.type !== 'skiplukk') {
    const tekst = [ordre.Varemerke, ordre.Produkt].filter(Boolean).join(' ');
    const detaljer = [ordre.Størrelse && `str. ${ordre.Størrelse}`, ordre.Farge].filter(Boolean).join(', ');
    return tekst ? [{ artikkel: [tekst, detaljer].filter(Boolean).join(', '), pris: '', antall: 1, rabatt: 0, rabattKr: 0 }] : [];
  }
  const med = (par || []).map((p, i) => ({ p, i })).filter(({ p }) => p.grunnprepp);
  if (!med.length) return [];
  let prepp = null;
  try {
    prepp = (await getSkiSkoyteTjenester()).find((t) => /^grunnprep\w*\s+nye\s+ski$/i.test(String(t.title || '').trim()));
  } catch (e) {
    prepp = null;
  }
  return med.map(({ p, i }) => ({
    artikkel: `${prepp?.title || 'Grunnprepp nye ski'}${p.hvem ? ` – ${p.hvem}` : par.length > 1 ? ` – par ${i + 1}` : ''}`,
    pris: prepp?.price ?? '',
    antall: 1,
    rabatt: 0,
    rabattKr: 0,
    // Verkstedarbeid: føres på servicen når skiene settes til service (src/lib/butikkvarer.js).
    verksted: true,
  }));
}

function Sammendrag({ linjer }) {
  const sum = oppsummering(linjer);
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <ul className="divide-y divide-slate-200">
        {linjer.map((l, i) => (
          <li key={i} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-3 py-1.5">
            <span className="min-w-0 flex-1 basis-48 [overflow-wrap:anywhere]">
              <span className="font-semibold text-slate-900">{l.artikkel || 'Vare'}</span>
              <span className="text-slate-600"> · {Math.max(1, Math.round(Number(l.antall) || 1))} stk</span>
              {l.verksted && <span className="text-slate-600"> · verkstedarbeid</span>}
              {!harPris(l) && <span className="block text-sm font-medium text-amber-800">Mangler pris</span>}
            </span>
            <span className="font-semibold text-slate-900">{kr(linjeTall(l).total)}</span>
          </li>
        ))}
      </ul>
      <p className="flex items-baseline justify-between gap-2 border-t-2 border-slate-300 px-3 py-1.5">
        <span className="font-semibold text-slate-900">Å betale{sum.rabattKr > 0 ? ` (rabatt ${kr(sum.rabattKr)})` : ''}</span>
        <span className="text-lg font-bold text-slate-900">{kr(sum.total)}</span>
      </p>
    </div>
  );
}

export default function OrdrePris({ ordre, kunde, par, onOrdreEndret }) {
  const egne = Array.isArray(ordre.prislinjer) ? ordre.prislinjer : [];
  const [tilbud, setTilbud] = useState(null); // godkjent tilbud: { produkter, offerRef, godkjent }
  const [redigerer, setRedigerer] = useState(false);
  const [linjer, setLinjer] = useState([]);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');
  const [melding, setMelding] = useState('');

  // Godkjent pristilbud på ordren: prisene derfra brukes når ordren ikke har egne.
  const tilbudId = ordre.tilbud?.status === 'godkjent' ? ordre.tilbud.id : null;
  useEffect(() => {
    if (!tilbudId) { setTilbud(null); return undefined; }
    let aktiv = true;
    fetch(`${API_BASE_URL}/offers/${tilbudId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((o) => {
        if (aktiv && o) setTilbud({ produkter: o.produkter || [], offerRef: o.offerRef, godkjent: o.quote?.approved?.at || null });
      })
      .catch(() => {});
    return () => { aktiv = false; };
  }, [tilbudId]);

  const fraTilbud = !egne.length && tilbud?.produkter?.length ? tilbud.produkter : null;
  const gjeldende = egne.length ? egne : fraTilbud || [];
  // Koblet til en service (OrdreServicer): verkstedarbeidet står på servicen og servicefakturaen,
  // så prisoversikten for ordren tar bare med varene.
  const tilService = (ordre.serviceIds || []).length > 0;
  const tilUtskrift = tilService ? gjeldende.filter((l) => !l.verksted) : gjeldende;
  const utelatt = gjeldende.length - tilUtskrift.length;

  const startRedigering = (fra) => {
    setLinjer(fra.map((l) => ({ ...l, rabattKr: l.rabattKr ?? l.totalRabattKr ?? 0 })));
    setRedigerer(true);
    setFeil('');
    setMelding('');
  };

  const lagre = async () => {
    setLagrer(true);
    setFeil('');
    try {
      const r = await fetch(`${API_BASE_URL}/orders/${ordre._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prislinjer: linjer }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.message || 'Klarte ikke å lagre prisene.');
      onOrdreEndret({ prislinjer: b.prislinjer || [] });
      setRedigerer(false);
      setMelding(b.tilbudOppdatert ? 'Prisene er lagret, og pristilbudet under har fått de nye prisene.'
        : b.tilbudUtdatert ? 'Prisene er lagret. Pristilbudet er allerede sendt og endres ikke; send tilbudet på nytt, så får kunden en ny versjon med ny lenke.'
        : 'Prisene er lagret.');
    } catch (e) {
      setFeil(e.message);
    } finally {
      setLagrer(false);
    }
  };

  const skrivUt = async () => {
    setFeil('');
    if (!tilUtskrift.length) { setFeil('Alt på ordren er verkstedarbeid. Skriv ut servicefakturaen på servicen.'); return; }
    const utenPris = tilUtskrift.filter((l) => !harPris(l));
    if (utenPris.length) { setFeil('Sett pris på alle varene før du skriver ut.'); return; }
    const ok = await skrivUtPrisoversikt({
      ordre,
      kunde,
      linjer: tilUtskrift,
      kilde: fraTilbud ? { tilbudRef: tilbud.offerRef, godkjent: tilbud.godkjent } : null,
    });
    if (!ok) setFeil('Nettleseren stoppet utskriftsvinduet. Tillat sprettoppvinduer for denne siden og prøv igjen.');
  };

  return (
    <section aria-labelledby="op-pris" className="space-y-2 rounded-2xl border-2 border-sky-200 bg-sky-50 p-3 sm:p-3">
      <h2 id="op-pris" className="text-base font-bold text-slate-900">Pris til kunden</h2>

      {redigerer ? (
        <>
          <VareLinjer idPrefiks="op" linjer={linjer} onEndre={setLinjer} />
          {linjer.length > 0 && <Sammendrag linjer={linjer} />}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={lagre} disabled={lagrer} className={knappPrimar}>
              <Ikon navn="hake" className="w-6 h-6" /> {lagrer ? 'Lagrer …' : 'Lagre prisene'}
            </button>
            <button type="button" onClick={() => setRedigerer(false)} disabled={lagrer} className={knappSekundar}>Avbryt</button>
          </div>
        </>
      ) : gjeldende.length ? (
        <>
          <p className="text-slate-800">
            {fraTilbud
              ? `Prisene fra det godkjente pristilbudet${tilbud.offerRef ? ` #${tilbud.offerRef}` : ''} brukes.`
              : 'Prisen kunden skal betale i kassa.'}
          </p>
          <Sammendrag linjer={gjeldende} />
          {tilService && (
            <p className="text-sm text-slate-800">
              Ordren er koblet til service. Servicefakturaen viser varene og verkstedarbeidet samlet
              {utelatt > 0 ? `; prisoversikten her tar bare med varene (${utelatt === 1 ? '1 linje' : `${utelatt} linjer`} med verkstedarbeid står på servicen)` : ''}.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={skrivUt} className={knappPrimar}>
              <Ikon navn="kvittering" className="w-6 h-6" /> Skriv ut prisoversikt
            </button>
            <button type="button" onClick={() => startRedigering(gjeldende)} className={knappSekundar}>
              <Ikon navn="blyant" className="w-5 h-5" /> Endre priser
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-slate-800">
            Ingen pris er satt. Prisen brukes på prisoversikten kunden tar med i kassa, og er linjene i pristilbudet
            under hvis kunden skal godkjenne prisen først.
          </p>
          <button type="button" onClick={async () => startRedigering(await startlinjer(ordre, par))} className={knappSekundar}>
            <Ikon navn="kvittering" className="w-5 h-5" /> Sett pris
          </button>
        </>
      )}

      {melding && <p role="status" className="font-medium text-emerald-800">{melding}</p>}
      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}
    </section>
  );
}
